import { useState, useEffect, useCallback, useRef } from 'react';

// Request manager to handle multiple concurrent requests
class RequestManager {
  constructor() {
    this.activeRequests = new Map();
    this.requestCounter = 0;
  }

  // Create a new request with automatic cancellation
  async request(url, options = {}) {
    const requestId = ++this.requestCounter;
    const abortController = new AbortController();
    
    // Store the request
    this.activeRequests.set(requestId, { abortController, url });
    
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      };

      const response = await fetch(url, {
        ...options,
        signal: abortController.signal,
        headers: {
          ...headers,
          ...options.headers
        }
      });

      // Check if request was cancelled
      if (!this.activeRequests.has(requestId)) {
        throw new Error('Request cancelled');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      // Remove from active requests
      this.activeRequests.delete(requestId);
      
      return result;
    } catch (error) {
      // Remove from active requests
      this.activeRequests.delete(requestId);
      
      if (error.name === 'AbortError') {
        throw new Error('Request cancelled');
      }
      throw error;
    }
  }

  // Cancel a specific request
  cancel(requestId) {
    const request = this.activeRequests.get(requestId);
    if (request) {
      request.abortController.abort();
      this.activeRequests.delete(requestId);
    }
  }

  // Cancel all active requests
  cancelAll() {
    this.activeRequests.forEach(({ abortController }) => {
      abortController.abort();
    });
    this.activeRequests.clear();
  }

  // Get count of active requests
  getActiveCount() {
    return this.activeRequests.size;
  }
}

// Global request manager instance
const globalRequestManager = new RequestManager();

// Hook for managing multiple requests with proper cleanup
export function useRequestManager() {
  const [requests, setRequests] = useState(new Map());
  const isMounted = useRef(true);
  const requestIds = useRef(new Set());

  useEffect(() => {
    isMounted.current = true;
    
    return () => {
      isMounted.current = false;
      // Cancel all requests for this component
      requestIds.current.forEach(id => {
        globalRequestManager.cancel(id);
      });
      requestIds.current.clear();
    };
  }, []);

  const makeRequest = useCallback(async (key, url, options = {}) => {
    if (!isMounted.current) return;

    // Cancel previous request with same key if exists
    const existingRequest = requests.get(key);
    if (existingRequest) {
      globalRequestManager.cancel(existingRequest.id);
    }

    // Set loading state
    setRequests(prev => new Map(prev.set(key, { 
      loading: true, 
      error: null, 
      data: null,
      id: null 
    })));

    try {
      const result = await globalRequestManager.request(url, options);
      
      if (isMounted.current) {
        setRequests(prev => new Map(prev.set(key, { 
          loading: false, 
          error: null, 
          data: result,
          id: null 
        })));
      }
    } catch (error) {
      if (isMounted.current) {
        setRequests(prev => new Map(prev.set(key, { 
          loading: false, 
          error: error.message, 
          data: null,
          id: null 
        })));
      }
    }
  }, [requests]);

  const cancelRequest = useCallback((key) => {
    const request = requests.get(key);
    if (request && request.id) {
      globalRequestManager.cancel(request.id);
    }
  }, [requests]);

  const getRequestState = useCallback((key) => {
    return requests.get(key) || { loading: false, error: null, data: null };
  }, [requests]);

  return {
    makeRequest,
    cancelRequest,
    getRequestState,
    requests: Array.from(requests.entries()).map(([key, state]) => ({ key, ...state }))
  };
}

// Hook for single request with automatic cleanup
export function useSingleRequest() {
  const [state, setState] = useState({ loading: false, error: null, data: null });
  const isMounted = useRef(true);
  const currentRequestId = useRef(null);

  useEffect(() => {
    isMounted.current = true;
    
    return () => {
      isMounted.current = false;
      if (currentRequestId.current) {
        globalRequestManager.cancel(currentRequestId.current);
      }
    };
  }, []);

  const makeRequest = useCallback(async (url, options = {}) => {
    if (!isMounted.current) return;

    // Cancel previous request if exists
    if (currentRequestId.current) {
      globalRequestManager.cancel(currentRequestId.current);
    }

    setState({ loading: true, error: null, data: null });

    try {
      const result = await globalRequestManager.request(url, options);
      
      if (isMounted.current) {
        setState({ loading: false, error: null, data: result });
      }
    } catch (error) {
      if (isMounted.current) {
        setState({ loading: false, error: error.message, data: null });
      }
    }
  }, []);

  return {
    ...state,
    makeRequest
  };
}

// Enhanced useFetch with better error handling and request management
export function useEnhancedFetch(url, options = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!!url);
  const [error, setError] = useState(null);
  const [reloadFlag, setReloadFlag] = useState(0);
  
  const isMounted = useRef(true);
  const currentRequestId = useRef(null);

  const fetchData = useCallback(async () => {
    if (!isMounted.current || !url) return;

    // Cancel previous request if exists
    if (currentRequestId.current) {
      globalRequestManager.cancel(currentRequestId.current);
    }

    setLoading(true);
    setError(null);

    try {
      const result = await globalRequestManager.request(url, options);
      
      if (isMounted.current) {
        setData(result);
      }
    } catch (error) {
      if (isMounted.current && error.message !== 'Request cancelled') {
        setError(error.message);
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
    }
  }, [url, JSON.stringify(options)]);

  useEffect(() => {
    isMounted.current = true;
    
    if (url) {
      fetchData();
    } else {
      setLoading(false);
      setData(null);
      setError(null);
    }
    
    return () => {
      isMounted.current = false;
      if (currentRequestId.current) {
        globalRequestManager.cancel(currentRequestId.current);
      }
    };
  }, [fetchData, url, reloadFlag]);

  const refetch = useCallback(() => {
    setReloadFlag(flag => flag + 1);
  }, []);

  return { data, loading, error, refetch };
} 